import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePaymentActions } from "hooks/usePaymentActions";
import dayjs from "utils/dayjs";
import { ConfirmDialog, DatePicker, FormField } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";

interface EditPendingPaymentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  handleYes: () => void;
}

export const EditPendingPaymentModal: React.FC<
  EditPendingPaymentModalProps
> = ({ open, setOpen, handleYes }) => {
  const { t } = useTranslation();

  if (!open) {
    return null;
  }

  return (
    <ConfirmDialog
      open={open}
      tone="primary"
      title={t("Edit Payment Confirmation")}
      message={t("EditPaymentConfirmationDescription")}
      confirmLabel={t("Yes")}
      cancelLabel={t("No")}
      onConfirm={handleYes}
      onCancel={() => setOpen(false)}
    />
  );
};

interface CancelPaymentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  paymentId?: string;
  handleUpdatePayments?: () => void;
}

export const CancelPaymentModal: React.FC<CancelPaymentModalProps> = ({
  open,
  setOpen,
  paymentId,
  handleUpdatePayments,
}) => {
  const { t } = useTranslation();
  const { cancelPayment, specificLoadingStates } = usePaymentActions({});

  if (!paymentId) {
    return null;
  }

  return (
    <ConfirmDialog
      open={open}
      tone="danger"
      title={t("Cancel Payment")}
      confirmLabel={t("Yes")}
      cancelLabel={t("No")}
      busy={specificLoadingStates.cancelLoading}
      onConfirm={async () => {
        try {
          await cancelPayment({ variables: { id: paymentId } });
          setOpen(false);
          handleUpdatePayments && handleUpdatePayments();
        } catch (err) {
          console.error(err);
        }
      }}
      onCancel={() => {
        if (specificLoadingStates.cancelLoading) return;
        setOpen(false);
      }}
      message={t("CancelPaymentConfirmationDescription")}
    />
  );
};

interface PayNowModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  paymentId?: string;
  handleUpdatePayments?: () => void;
}

export const PayNowModal: React.FC<PayNowModalProps> = ({
  open,
  setOpen,
  paymentId,
  handleUpdatePayments,
}) => {
  const { t } = useTranslation();
  const { payPaymentNow, specificLoadingStates } = usePaymentActions({
    paymentId,
  });

  if (!paymentId) {
    return null;
  }

  return (
    <ConfirmDialog
      open={open}
      tone="primary"
      title={t("Pay Payment Now")}
      message={t("PayPaymentNowConfirmationDescription")}
      confirmLabel={t("Yes")}
      cancelLabel={t("No")}
      onConfirm={async () => {
        try {
          await payPaymentNow({ id: paymentId });
          setOpen(false);
          handleUpdatePayments && handleUpdatePayments();
        } catch (err) {
          console.error(err);
        }
      }}
      // `busy` disables Cancel and swallows the backdrop, but the close X
      // and Escape still fire: hold the gate here so a payment in flight
      // cannot be dismissed into an unknown outcome.
      onCancel={() => {
        if (
          specificLoadingStates.loadingPayment ||
          specificLoadingStates.savingPayment
        ) {
          return;
        }
        setOpen(false);
      }}
      busy={
        specificLoadingStates.loadingPayment ||
        specificLoadingStates.savingPayment
      }
    />
  );
};

interface MarkAsPaidModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  paymentId?: string;
  handleUpdatePayments?: () => void;
}

export const MarkAsPaidModal: React.FC<MarkAsPaidModalProps> = ({
  open,
  setOpen,
  paymentId,
  handleUpdatePayments,
}) => {
  const { t } = useTranslation();
  const { markAsPaid, specificLoadingStates } = usePaymentActions({});
  const [paidDate, setPaidDate] = useState<Date | null>(new Date());

  if (!paymentId) {
    return null;
  }

  function isValidDate(date: Date | null) {
    return !!date && !isNaN(date.getTime());
  }

  return (
    <ConfirmDialog
      open={open}
      tone="primary"
      title={t("Mark Payment as Paid")}
      confirmLabel={t("Yes")}
      cancelLabel={t("No")}
      message={
        <Stack gap="var(--space-3)">
          {t("MarkPaymentAsPaidConfirmationDescription")}
          <FormField label={t("Date Paid")}>
            <DatePicker
              aria-label={t("Date Paid")}
              value={paidDate ? dayjs(paidDate) : null}
              onChange={(date) => setPaidDate(date ? date.toDate() : null)}
              maxDate={dayjs()}
            />
          </FormField>
        </Stack>
      }
      onConfirm={async () => {
        try {
          await markAsPaid({
            variables: {
              id: paymentId,
              actualPaymentDate: paidDate && new Date(paidDate).getTime(),
            },
          });
          setOpen(false);
          handleUpdatePayments && handleUpdatePayments();
        } catch (err) {
          console.error(err);
        }
      }}
      busy={specificLoadingStates.markingAsPaid}
      onCancel={() => {
        if (specificLoadingStates.markingAsPaid) return;
        setOpen(false);
      }}
      confirmDisabled={!isValidDate(paidDate)}
    />
  );
};
